import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Search,
  Filter,
  Download,
  Package,
  Calendar,
  ChevronDown,
  RefreshCw,
  X,
  CheckCircle,
  AlertCircle,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "../Profile/Header";
import OrderCard from "./OrderCard";
import OrderDetailsModal from "./OrderDetailsModal";
import ReviewModal from "./ReviewModal";
import ReturnModal from "./ReturnModal";
import OrderCancelModal from "./OrderCancelModal";
import TopPicks from "../Profile/TopPicks";
import { useOrders } from "../context/OrdersContext";
import { useCart } from "../context/CartContext";
import { useCustomerReviews } from "../context/CustomerReviewContext";
import { useReturns } from "../context/ReturnContext";
import { useOrderCancel } from "../context/OrderCancelContext";

// Modal Component
const Modal = ({
  isOpen,
  onClose,
  title,
  message,
  type = "info", // 'success', 'error', 'warning', 'info', 'confirm'
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  showCancel = false,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case "error":
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      case "warning":
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      case "confirm":
        return <AlertCircle className="w-6 h-6 text-teal-800" />;
      default:
        return <Info className="w-6 h-6 text-blue-500" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case "success":
        return {
          bg: "bg-green-50",
          border: "border-green-200",
          button: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
        };
      case "error":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          button: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
        };
      case "warning":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-200",
          button: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
        };
      case "confirm":
        return {
          bg: "bg-teal-100",
          border: "border-teal-700",
          button: "bg-teal-800 hover:bg-teal-600 focus:ring-teal-500",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          button: "bg-gray-600 hover:bg-gray-700 focus:ring-gray-500",
        };
    }
  };

  const colors = getColors();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getIcon()}
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className={`p-6 ${colors.bg} ${colors.border} border-l-4`}>
          <p className="text-gray-700 leading-relaxed">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 bg-gray-50">
          {showCancel && (
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`${showCancel ? "flex-1" : "w-full"} px-4 py-2 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${colors.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderHistory = () => {
  const { orders = [], loading, error, refreshOrders } = useOrders();
  const { addToCart } = useCart();
  const { fetchOrderReview } = useCustomerReviews();
  const { clearError: clearReturnError } = useReturns();
  const { clearError: clearCancelError } = useOrderCancel();
  const navigate = useNavigate();

  // Debug logging
  console.log("OrderHistory - Orders:", orders);
  console.log("OrderHistory - Loading:", loading);
  console.log("OrderHistory - Error:", error);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelOrder, setCancelOrder] = useState(null);

  // Refs for dropdown containers
  const statusDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
    onCancel: null,
    showCancel: false,
    confirmText: "OK",
    cancelText: "Cancel",
  });

  // Reviews are now fetched individually when needed - no bulk fetching

  // Refresh orders every time the user navigates to this page
  useEffect(() => {
    refreshOrders();
  }, [refreshOrders]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowStatusDropdown(false);
        setShowDateDropdown(false);
        setShowSortDropdown(false);
      }
    };

    if (showStatusDropdown || showDateDropdown || showSortDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showStatusDropdown, showDateDropdown, showSortDropdown]);

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  // Helper function to get full datetime for comparison
  const getOrderDateTime = (order) => {
    // If order has both date and time, combine them
    if (order.date && order.time) {
      // Handle different date formats
      let dateStr = order.date;
      let timeStr = order.time;

      // If date is already a full datetime string, use it directly
      if (dateStr.includes("T") || dateStr.includes(" ")) {
        return new Date(dateStr);
      }

      // Combine date and time
      const combinedDateTime = `${dateStr} ${timeStr}`;
      return new Date(combinedDateTime);
    }

    // If only date is available, use it
    if (order.date) {
      return new Date(order.date);
    }

    // If order has a timestamp field
    if (order.timestamp) {
      return new Date(order.timestamp);
    }

    // If order has createdAt field
    if (order.createdAt) {
      return new Date(order.createdAt);
    }

    // Fallback to current time
    return new Date();
  };

  // Helper function to filter orders by date range
  const filterOrdersByDate = (orders, dateFilter) => {
    if (dateFilter === "all") {
      return orders;
    }

    const now = new Date();
    return orders.filter((order) => {
      const orderDate = getOrderDateTime(order);
      const diffTime = now - orderDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      switch (dateFilter) {
        case "week":
          return diffDays <= 7;
        case "month":
          return diffDays <= 30;
        case "3months":
          return diffDays <= 90;
        case "year":
          return diffDays <= 365;
        default:
          return true;
      }
    });
  };

  // Calculate status options with dynamic counts based on date filter
  const statusOptions = useMemo(() => {
    // First filter orders by date range
    const dateFilteredOrders = filterOrdersByDate(orders, dateFilter);

    return [
      { value: "all", label: "All Orders", count: dateFilteredOrders.length },
      {
        value: "Placed",
        label: "Order Placed",
        count: dateFilteredOrders.filter((o) => o.status === "Placed").length,
      },
      {
        value: "awaiting_process",
        label: "Awaiting Process",
        count: dateFilteredOrders.filter((o) =>
          ["Processing", "Printing Started", "Production Completed", "Quality Check", "quality_check", "Packaging"].includes(o.status)
        ).length,
      },
      {
        value: "Shipped",
        label: "Shipped",
        count: dateFilteredOrders.filter((o) => o.status === "Shipped").length,
      },
      {
        value: "Delivered",
        label: "Delivered",
        count: dateFilteredOrders.filter((o) => o.status === "Delivered").length,
      },
      {
        value: "Completed",
        label: "Completed",
        count: dateFilteredOrders.filter((o) => o.status === "Completed").length,
      },
      {
        value: "Return Requested",
        label: "Return Requested",
        count: dateFilteredOrders.filter((o) => o.status === "Return Requested" || o.status === "return_requested").length,
      },
      {
        value: "Return Approved",
        label: "Return Approved",
        count: dateFilteredOrders.filter((o) => o.status === "Return Approved" || o.status === "return_approved").length,
      },
      {
        value: "Return Rejected",
        label: "Return Rejected",
        count: dateFilteredOrders.filter((o) => o.status === "Return Rejected" || o.status === "return_rejected").length,
      },
      {
        value: "Returned",
        label: "Received Product",
        count: dateFilteredOrders.filter((o) => o.status === "Returned").length,
      },
      {
        value: "Inspection Pending",
        label: "Inspection",
        count: dateFilteredOrders.filter((o) => o.status === "Inspection Pending" || o.status === "inspection_pending").length,
      },
      {
        value: "Partial Refund",
        label: "Partial Refund",
        count: dateFilteredOrders.filter((o) => o.status === "Partial Refund" || o.status === "partial_refund").length,
      },
      {
        value: "Replacement Processing",
        label: "Replacement",
        count: dateFilteredOrders.filter((o) => o.status === "Replacement Processing" || o.status === "replacement_processing").length,
      },
      {
        value: "Refund Processing",
        label: "Refund Processing",
        count: dateFilteredOrders.filter((o) => o.status === "Refund Processing" || o.status === "refund_processing").length,
      },
      {
        value: "Cancellation Requested",
        label: "Cancel Requested",
        count: dateFilteredOrders.filter((o) => o.status === "Cancellation Requested" || o.status === "cancellation_requested" || o.status === "cancel_requested").length,
      },
      {
        value: "Cancelled",
        label: "Cancelled",
        count: dateFilteredOrders.filter((o) => o.status === "Cancelled").length,
      },
      {
        value: "Rejected",
        label: "Rejected",
        count: dateFilteredOrders.filter((o) => o.status === "Rejected").length,
      },
      {
        value: "Returned (No Refund)",
        label: "No Refund",
        count: dateFilteredOrders.filter((o) => o.status === "Returned (No Refund)" || o.status === "returned_no_refund").length,
      },
      {
        value: "Refunded",
        label: "Refunded",
        count: dateFilteredOrders.filter((o) => o.status === "Refund" || o.status === "Refunded").length,
      },
    ];
  }, [orders, dateFilter]);

  const dateOptions = [
    { value: "all", label: "All Time" },
    { value: "week", label: "Last Week" },
    { value: "month", label: "Last Month" },
    { value: "3months", label: "Last 3 Months" },
    { value: "year", label: "Last Year" },
  ];

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "amount-high", label: "Amount: High to Low" },
    { value: "amount-low", label: "Amount: Low to High" },
  ];

  const filteredAndSortedOrders = useMemo(() => {
    let filtered = orders.filter((order) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.items || []).some((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );

      // Status filter - handle both exact matches and case variations
      const matchesStatus =
        statusFilter === "all" ||
        order.status === statusFilter ||
        (statusFilter === "awaiting_process" && ["Processing", "Printing Started", "Production Completed", "Quality Check", "quality_check", "Packaging"].includes(order.status)) ||
        (statusFilter === "Refunded" && (order.status === "Refund" || order.status === "Refunded")) ||
        (statusFilter === "Return Requested" && (order.status === "Return Requested" || order.status === "return_requested")) ||
        (statusFilter === "Return Approved" && (order.status === "Return Approved" || order.status === "return_approved")) ||
        (statusFilter === "Return Rejected" && (order.status === "Return Rejected" || order.status === "return_rejected")) ||
        (statusFilter === "Inspection Pending" && (order.status === "Inspection Pending" || order.status === "inspection_pending")) ||
        (statusFilter === "Partial Refund" && (order.status === "Partial Refund" || order.status === "partial_refund")) ||
        (statusFilter === "Replacement Processing" && (order.status === "Replacement Processing" || order.status === "replacement_processing")) ||
        (statusFilter === "Refund Processing" && (order.status === "Refund Processing" || order.status === "refund_processing")) ||
        (statusFilter === "Cancellation Requested" && (order.status === "Cancellation Requested" || order.status === "cancellation_requested" || order.status === "cancel_requested")) ||
        (statusFilter === "Returned (No Refund)" && (order.status === "Returned (No Refund)" || order.status === "returned_no_refund"));

      // Date filter
      let matchesDate = true;
      if (dateFilter !== "all") {
        const orderDate = getOrderDateTime(order);
        const now = new Date();
        const diffTime = now - orderDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case "week":
            matchesDate = diffDays <= 7;
            break;
          case "month":
            matchesDate = diffDays <= 30;
            break;
          case "3months":
            matchesDate = diffDays <= 90;
            break;
          case "year":
            matchesDate = diffDays <= 365;
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    // Sort orders with enhanced date-time comparison
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          // Sort by full datetime (newest first)
          const dateTimeA = getOrderDateTime(a);
          const dateTimeB = getOrderDateTime(b);
          return dateTimeB - dateTimeA;

        case "oldest":
          // Sort by full datetime (oldest first)
          const dateTimeA_old = getOrderDateTime(a);
          const dateTimeB_old = getOrderDateTime(b);
          return dateTimeA_old - dateTimeB_old;

        case "amount-high":
          return b.total - a.total;

        case "amount-low":
          return a.total - b.total;

        default:
          return 0;
      }
    });

    return filtered;
  }, [orders, searchTerm, statusFilter, dateFilter, sortBy]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    setReviewOrder(null);
  };

  const handleCloseReturnModal = () => {
    setIsReturnModalOpen(false);
    setReturnOrder(null);
    clearReturnError();
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    setCancelOrder(null);
    clearCancelError();
  };

  // Modal helper functions
  const showModal = (config) => {
    setModal({
      isOpen: true,
      type: "info",
      confirmText: "OK",
      cancelText: "Cancel",
      showCancel: false,
      onConfirm: null,
      onCancel: null,
      ...config,
    });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  const showSuccessModal = (message, onConfirm = null) => {
    showModal({
      type: "success",
      title: "Success",
      message,
      onConfirm: onConfirm || closeModal,
    });
  };

  const showErrorModal = (message) => {
    showModal({
      type: "error",
      title: "Error",
      message,
      onConfirm: closeModal,
    });
  };

  const showConfirmModal = (message, onConfirm, onCancel = null) => {
    showModal({
      type: "confirm",
      title: "Confirm Action",
      message,
      showCancel: true,
      confirmText: "Yes",
      cancelText: "No",
      onConfirm,
      onCancel: onCancel || closeModal,
    });
  };

  const handleReorder = (order) => {
    try {
      // Add each item from the order to the cart
      let itemsAdded = 0;

      order.items.forEach((item) => {
        // Skip unavailable items
        if (item.isUnavailable) {
          return;
        }

        // Create a product object that matches the expected format for addToCart
        const productForCart = {
          id: item.id,
          name: item.name,
          price: item.price,
          offerPrice: item.price, // Use the same price as offer price
          image: item.image,
          categoryId: item.category,
          description: `Reordered from Order #${order.orderNumber}`,
          images: [item.image],
        };

        // Add the item to cart with its original quantity and customization
        addToCart(productForCart, item.quantity, item.customization || {});
        itemsAdded++;
      });

      if (itemsAdded === 0) {
        showErrorModal(
          "No items could be added to cart. All items in this order are no longer available.",
        );
        return;
      }

      // Show success message with option to go to cart
      showConfirmModal(
        `${itemsAdded} item${itemsAdded !== 1 ? "s" : ""} added to cart successfully! ${order.items.length - itemsAdded > 0 ? `(${order.items.length - itemsAdded} unavailable items were skipped)` : ""} Would you like to view your cart now?`,
        () => {
          closeModal();
          navigate("/cart");
        },
        closeModal,
      );
    } catch (error) {
      console.error("Error adding items to cart:", error);
      showErrorModal("Failed to add items to cart. Please try again.");
    }
  };

  const handleRefreshOrders = async () => {
    try {
      // Clear all filters when refreshing
      setSearchTerm("");
      setStatusFilter("all");
      setDateFilter("all");
      setSortBy("newest");
      setShowFilters(false);

      await refreshOrders();
      showSuccessModal("Orders refreshed successfully!");
    } catch (error) {
      console.error("Failed to refresh orders:", error);
      showErrorModal("Failed to refresh orders. Please try again.");
    }
  };

  const handleDownloadInvoice = (order) => {
    console.log("Downloading invoice for:", order.orderNumber);
    showSuccessModal(
      "Invoice download started! Please check your downloads folder.",
    );
  };

  const handleTrackOrder = (order) => {
    console.log("Tracking order:", order.trackingNumber);
    showModal({
      type: "info",
      title: "Order Tracking",
      message: `Tracking information for Order #${order.orderNumber} will be displayed here. Tracking ID: ${order.trackingNumber}`,
      onConfirm: closeModal,
    });
  };

  const handleReview = async (order) => {
    console.log("Opening review modal for order:", order.id);

    // Fetch reviews for this specific order when review button is clicked
    try {
      await fetchOrderReview(order.id);
    } catch (error) {
      console.error("Failed to fetch reviews for order:", order.id, error);
      // Continue to open modal even if fetch fails - user can still create a review
    }

    setReviewOrder(order);
    setIsReviewModalOpen(true);
  };

  const handleReturnRequest = (order) => {
    setReturnOrder(order);
    setIsReturnModalOpen(true);
  };

  const handleCancelRequest = (order) => {
    setCancelOrder(order);
    setIsCancelModalOpen(true);
  };

  const handleReviewSubmitted = (orderId) => {
    console.log("Review submitted for order:", orderId);

    // Close the review modal first
    setIsReviewModalOpen(false);
    setReviewOrder(null);

    // Show success message
    showSuccessModal(
      "Review submitted successfully! Thank you for your feedback.",
      () => {
        closeModal();
        // Refresh reviews for this specific order
        fetchOrderReview(orderId);
      },
    );
  };

  const handleReturnSubmitted = (orderId) => {
    console.log("Return request submitted for order:", orderId);

    // Close the return modal first
    setIsReturnModalOpen(false);
    setReturnOrder(null);

    // Show success message
    showSuccessModal(
      "Return request submitted successfully! We'll review your request and get back to you soon.",
      () => {
        closeModal();
        // Refresh orders to get updated status
        refreshOrders();
      },
    );
  };

  const handleCancelSubmitted = (orderId) => {
    console.log("Cancel request submitted for order:", orderId);

    // Close the cancel modal first
    setIsCancelModalOpen(false);
    setCancelOrder(null);

    // Show success message
    showSuccessModal(
      "Cancellation request submitted successfully! We'll review your request and get back to you soon.",
      () => {
        closeModal();
        // Refresh orders to get updated status
        refreshOrders();
      },
    );
  };

  const getOrderStats = () => {
    const totalOrders = orders.length;
    const totalSpent = orders.reduce((sum, order) => sum + order.total, 0);
    const deliveredOrders = orders.filter(
      (o) => o.status === "Delivered" || o.status === "Completed",
    ).length;
    const activeOrders = orders.filter((o) =>
      ["Processing", "Printing Started", "Production Completed", "Quality Check", "quality_check", "Packaging", "Shipped"].includes(o.status),
    ).length;

    return { totalOrders, totalSpent, deliveredOrders, activeOrders };
  };

  const stats = getOrderStats();

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return !!(token && user);
  };

  return (
    <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 pb-8 w-full">
      {/* Header */}
      <Header
        activeSection="orders"
        onSearch={handleSearch}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* Main Content */}
      <main className="relative mx-auto w-[90%] lg:w-[80%] xl:w-[75%] 2xl:w-[70%] mt-6 md:mt-1 px-4 md:px-0">
        {/* Page Header */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center w-full text-teal-800 mb-6 md:mb-4  mx-auto">
          <div className="flex items-center gap-4">
            <Package className="w-8 h-8 text-teal-600" />
            <h1 className="text-2xl md:text-3xl lg:text-4xl tracking-wider font-bold text-teal-800">
              Order History
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleRefreshOrders}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              title="Refresh orders"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
              <span>
                {searchTerm
                  ? `${filteredAndSortedOrders.length} of ${orders.length}`
                  : filteredAndSortedOrders.length}{" "}
                orders
                {searchTerm && ` matching "${searchTerm}"`}
              </span>
            </div>
          </div>
        </header>

        {/* Search and Filters */}
        <section
          className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-6 mb-4 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300 transform hover:-translate-y-1 relative z-[10]"
          aria-label="Search and filter orders"
        >
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg p-2"
            aria-expanded={showFilters}
            aria-controls="filter-options"
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          {/* Filter Options */}
          {showFilters && (
            <div
              id="filter-options"
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200"
            >
              {/* Status Filter */}
              <div className="relative" ref={statusDropdownRef}>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Order Status
                </label>
                <button
                  id="status-filter"
                  type="button"
                  onClick={() => {
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowDateDropdown(false);
                    setShowSortDropdown(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 bg-white text-left flex items-center justify-between"
                  aria-expanded={showStatusDropdown}
                  aria-haspopup="listbox"
                >
                  <span className="truncate">
                    {statusOptions.find(option => option.value === statusFilter)?.label || 'Select Status'}
                    ({statusOptions.find(option => option.value === statusFilter)?.count || 0})
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`}
                  />
                </button>

                {showStatusDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl max-h-52 overflow-y-auto z-[999999]">
                    <ul role="listbox" className="py-1">
                      {statusOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            type="button"
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors ${
                              statusFilter === option.value ? 'bg-teal-50 text-teal-800 font-medium' : 'text-gray-900'
                            }`}
                            onClick={() => {
                              setStatusFilter(option.value);
                              setShowStatusDropdown(false);
                            }}
                            role="option"
                            aria-selected={statusFilter === option.value}
                          >
                            <span className="block truncate">
                              {option.label} ({option.count})
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="relative" ref={dateDropdownRef}>
                <label
                  htmlFor="date-filter"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Date Range
                </label>
                <button
                  id="date-filter"
                  type="button"
                  onClick={() => {
                    setShowDateDropdown(!showDateDropdown);
                    setShowStatusDropdown(false);
                    setShowSortDropdown(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 bg-white text-left flex items-center justify-between"
                  aria-expanded={showDateDropdown}
                  aria-haspopup="listbox"
                >
                  <span className="truncate">
                    {dateOptions.find(option => option.value === dateFilter)?.label || 'Select Date Range'}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showDateDropdown ? 'rotate-180' : ''}`}
                  />
                </button>

                {showDateDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl max-h-52 overflow-y-auto z-[999999]">
                    <ul role="listbox" className="py-1">
                      {dateOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            type="button"
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors ${
                              dateFilter === option.value ? 'bg-teal-50 text-teal-800 font-medium' : 'text-gray-900'
                            }`}
                            onClick={() => {
                              setDateFilter(option.value);
                              setShowDateDropdown(false);
                            }}
                            role="option"
                            aria-selected={dateFilter === option.value}
                          >
                            <span className="block truncate">
                              {option.label}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Sort Filter */}
              <div className="relative" ref={sortDropdownRef}>
                <label
                  htmlFor="sort-filter"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Sort By
                </label>
                <button
                  id="sort-filter"
                  type="button"
                  onClick={() => {
                    setShowSortDropdown(!showSortDropdown);
                    setShowStatusDropdown(false);
                    setShowDateDropdown(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 bg-white text-left flex items-center justify-between"
                  aria-expanded={showSortDropdown}
                  aria-haspopup="listbox"
                >
                  <span className="truncate">
                    {sortOptions.find(option => option.value === sortBy)?.label || 'Select Sort Option'}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`}
                  />
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl max-h-48 overflow-y-auto z-[999999]">
                    <ul role="listbox" className="py-1">
                      {sortOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            type="button"
                            className={`w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors ${
                              sortBy === option.value ? 'bg-teal-50 text-teal-800 font-medium' : 'text-gray-900'
                            }`}
                            onClick={() => {
                              setSortBy(option.value);
                              setShowSortDropdown(false);
                            }}
                            role="option"
                            aria-selected={sortBy === option.value}
                          >
                            <span className="block truncate">
                              {option.label}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12 md:py-16">
            <div className="max-w-md mx-auto px-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <RefreshCw className="w-10 h-10 md:w-12 md:h-12 text-gray-400 animate-spin" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                Loading your orders...
              </h2>
              <p className="text-gray-600 text-sm md:text-base">
                Please wait while we fetch your order history.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12 md:py-16">
            <div className="max-w-md mx-auto px-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 md:w-12 md:h-12 text-red-500" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                Unable to load orders
              </h2>
              <p className="text-gray-600 mb-6 text-sm md:text-base">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleRefreshOrders}
                  className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                >
                  Try Again
                </button>
                {error.includes("login") && (
                  <button
                    onClick={() => navigate("/login")}
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-sm md:text-base"
                  >
                    Go to Login
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Orders List */}
        {!loading && !error && (
          <section
            aria-label={`${filteredAndSortedOrders.length} orders found`}
          >
            {filteredAndSortedOrders.length === 0 ? (
              <div className="text-center py-12 md:py-16">
                <div className="max-w-md mx-auto px-4">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <Package className="w-10 h-10 md:w-12 md:h-12 text-gray-400" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                    {searchTerm ||
                    statusFilter !== "all" ||
                    dateFilter !== "all"
                      ? "No orders found"
                      : !isAuthenticated()
                        ? "Please login to view orders"
                        : "No orders yet"}
                  </h2>
                  <p className="text-gray-600 mb-6 text-sm md:text-base">
                    {searchTerm ||
                    statusFilter !== "all" ||
                    dateFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : !isAuthenticated()
                        ? "You need to be logged in to view your order history."
                        : "Start shopping to see your orders here."}
                  </p>
                  {!isAuthenticated() ? (
                    <button
                      onClick={() => navigate("/login")}
                      className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                    >
                      Go to Login
                    </button>
                  ) : searchTerm ||
                    statusFilter !== "all" ||
                    dateFilter !== "all" ? (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("all");
                        setDateFilter("all");
                      }}
                      className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                    >
                      Clear Filters
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate("/")}
                      className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm md:text-base"
                    >
                      Start Shopping
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4 relative z-0">
                {filteredAndSortedOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onClick={() => handleOrderClick(order)}
                    onReorder={() => handleReorder(order)}
                    onReturnRequest={() => handleReturnRequest(order)}
                    onReview={() => handleReview(order)}
                    onCancelRequest={() => handleCancelRequest(order)}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseModal}
        order={selectedOrder}
        onReorder={handleReorder}
        onDownloadInvoice={handleDownloadInvoice}
        onTrackOrder={handleTrackOrder}
      />

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={handleCloseReviewModal}
        order={reviewOrder}
        onReviewSubmitted={handleReviewSubmitted}
      />

      {/* Return Modal */}
      <ReturnModal
        isOpen={isReturnModalOpen}
        onClose={handleCloseReturnModal}
        order={returnOrder}
        onReturnSubmitted={handleReturnSubmitted}
      />

      {/* Cancel Modal */}
      <OrderCancelModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        order={cancelOrder}
        onCancelSubmitted={handleCancelSubmitted}
      />

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        showCancel={modal.showCancel}
      />

      {/* Custom CSS for scrollable dropdown */}
      <style jsx>{`
        /* Custom scrollbar for the dropdown */
        .max-h-48::-webkit-scrollbar {
          width: 6px;
        }

        .max-h-48::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .max-h-48::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .max-h-48::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Firefox scrollbar */
        .max-h-48 {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
      `}</style>
    </div>
  );
};

export default OrderHistory;
